import { useState, useEffect, useCallback, useMemo } from 'react';

var config = {
    attributes: true,
    characterData: true,
    subtree: true,
    childList: true
};
/**
 *
 * useMutationObserverRef hook
 *
 * Returns a mutation observer for a React Ref and fires a callback
 *
 * @param {MutationCallback} callback Function that needs to be fired on mutation
 * @param {MutationObserverInit} options
 */
function useMutationObserverRef(callback, options) {
    if (options === void 0) { options = config; }
    var _a = useState(null), node = _a[0], setNode = _a[1];
    useEffect(function () {
        // Create an observer instance linked to the callback function
        if (node) {
            var observer_1 = new MutationObserver(callback);
            // Start observing the target node for configured mutations
            observer_1.observe(node, options);
            return function () {
                observer_1.disconnect();
            };
        }
    }, [node, callback, options]);
    var ref = useCallback(function (node) {
        setNode(node);
    }, []);
    return [ref];
}

/**
 * Credit to material-ui for this snippet
 */
function setRef(ref, value) {
    if (typeof ref === "function") {
        ref(value);
    }
    else if (ref) {
        ref.current = value;
    }
}
function useForkRef(refA, refB) {
    /**
     * This will create a new function if the ref props change and are defined.
     * This means react will call the old forkRef with `null` and the new forkRef
     * with the ref. Cleanup naturally emerges from this behavior
     */
    return useMemo(function () {
        if (refA == null && refB == null) {
            return null;
        }
        return function (refValue) {
            setRef(refA, refValue);
            setRef(refB, refValue);
        };
    }, [refA, refB]);
}

/**
 * useBoundingclientrectRef hook
 *
 * @param ref The React ref whose ClientRect is needed
 * @return ClientRect
 */
function getBoundingClientRect(element) {
    return element.getBoundingClientRect();
}
function useBoundingclientrectRef() {
    var _a = useState(null), value = _a[0], setValue = _a[1];
    var _b = useState(null), node = _b[0], setNode = _b[1];
    var update = useCallback(function () {
        setValue(node ? getBoundingClientRect(node) : null);
    }, [node]);
    useEffect(function () {
        update();
    }, [node]);
    var ref = useCallback(function (node) {
        setNode(node);
    }, []);
    var mutationObserverRef = useMutationObserverRef(update)[0];
    var forkedRef = useForkRef(ref, mutationObserverRef);
    return [forkedRef, value, update];
}

export default useBoundingclientrectRef;
//# sourceMappingURL=index.esm.js.map
